import React, { useState, useCallback } from "react";
import { z } from "zod";
import { useFormContext } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { StepProps } from "@/components/multi-step-form";
import {
  AmenitiesForm,
  FormAmenityOutput,
  AmenitiesData,
} from "@/components/amenities-form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export const caracteristicasEditSchema = z.object({
  selectedAmenities: z
    .array(z.any(), {
      required_error: 'O campo "Amenidades" é obrigatório',
    })
    .min(1, "Selecione pelo menos uma amenidade"),
});

export type CaracteristicasEditData = z.infer<typeof caracteristicasEditSchema>;

// Global validation function for step navigation
let globalValidateCaracteristicasEdit:
  | ((
      amenities: any[],
    ) => Promise<{ valid: boolean; errors?: Record<string, string[]> }>)
  | null = null;

interface StepCaracteristicasEditProps extends StepProps {
  amenitiesData?: AmenitiesData | null;
}

export const StepCaracteristicasEdit: React.FC<
  StepCaracteristicasEditProps
> = ({ errors, registerField, amenitiesData: propAmenitiesData }) => {
  const { watch, setValue } = useFormContext();
  const watchedValues = watch();
  const [backendAmenitiesLoaded, setBackendAmenitiesLoaded] = useState(false);

  // State for alert modals
  const [wifiAlert, setWifiAlert] = useState({
    isOpen: false,
    resolve: null as ((proceed: boolean) => void) | null,
  });

  const [airCondAlert, setAirCondAlert] = useState({
    isOpen: false,
    resolve: null as ((proceed: boolean) => void) | null,
  });

  const selectedAmenities: FormAmenityOutput[] =
    watchedValues.selectedAmenities || [];

  // Initialize backend amenities in the form if they exist and haven't been loaded yet
  React.useEffect(() => {
    if (
      !backendAmenitiesLoaded &&
      propAmenitiesData &&
      propAmenitiesData.amenities
    ) {
      console.log("Loading backend amenities:", propAmenitiesData);

      // Transform backend amenities to FormAmenityOutput format
      const backendSelectedAmenities: FormAmenityOutput[] = [];

      propAmenitiesData.amenities.forEach((group) => {
        group.amenities.forEach((amenity) => {
          // Only include amenities that have a status other than 'not-included'
          if (amenity.status && amenity.status !== "not-included") {
            const amenityOutput = {
              amenityId: amenity.id,
              status: amenity.status,
              attributes: {},
              slug: amenity.slug,
              name: amenity.name,
              description: amenity.description,
            } as FormAmenityOutput & {
              slug?: string;
              name?: string;
              description?: string;
            };

            // Transform attributes if they exist and have content
            if (amenity.attributes && amenity.attributes.length > 0) {
              amenity.attributes.forEach((attr) => {
                if (attr.content && attr.content.trim() !== "") {
                  if (!amenityOutput.attributes) {
                    amenityOutput.attributes = {};
                  }
                  amenityOutput.attributes[attr.name] = attr.content;
                }
              });
            }

            backendSelectedAmenities.push(amenityOutput);
          }
        });
      });

      if (backendSelectedAmenities.length > 0) {
        console.log(
          "Setting backend selected amenities:",
          backendSelectedAmenities,
        );
        setValue("selectedAmenities", backendSelectedAmenities);
      }

      setBackendAmenitiesLoaded(true);
    }
  }, [propAmenitiesData, backendAmenitiesLoaded, setValue]);

  // Use prop data instead of fetching
  const amenitiesData = propAmenitiesData;
  const isLoadingAmenities = propAmenitiesData === undefined;
  const amenitiesError = null; // Error handling is done in parent component

  // Debug log to see the data
  React.useEffect(() => {
    if (selectedAmenities.length > 0) {
      console.log("Current selected amenities:", selectedAmenities);
    }
  }, [selectedAmenities]);

  const handleAmenitiesChange = (amenities: FormAmenityOutput[]) => {
    setValue("selectedAmenities", amenities);
  };

  // Check if WiFi is not included (unavailable) - stable callback
  const checkWifiAlert = useCallback(
    (amenities: FormAmenityOutput[]): Promise<boolean> => {
      return new Promise((resolve) => {
        if (!amenitiesData) {
          resolve(true);
          return;
        }

        // Find WiFi amenity by slug
        const wifiAmenity = amenities.find((a) => {
          const amenityData = amenitiesData.amenities
            .flatMap((group) => group.amenities)
            .find((am) => am.id === a.amenityId);
          return amenityData?.slug === "wi-fi";
        });
        const isWifiUnavailable =
          !wifiAmenity || wifiAmenity.status === "not-included";

        if (isWifiUnavailable) {
          setWifiAlert({
            isOpen: true,
            resolve: resolve,
          });
        } else {
          resolve(true);
        }
      });
    },
    [amenitiesData],
  );

  // Check if air conditioning is not included (unavailable) - stable callback
  const checkAirCondAlert = useCallback(
    (amenities: FormAmenityOutput[]): Promise<boolean> => {
      return new Promise((resolve) => {
        if (!amenitiesData) {
          resolve(true);
          return;
        }

        // Find air conditioning amenity by slug
        const airCondAmenity = amenities.find((a) => {
          const amenityData = amenitiesData.amenities
            .flatMap((group) => group.amenities)
            .find((am) => am.id === a.amenityId);
          return amenityData?.slug === "ar-condicionado";
        });
        const isAirCondUnavailable =
          !airCondAmenity || airCondAmenity.status === "not-included";

        if (isAirCondUnavailable) {
          setAirCondAlert({
            isOpen: true,
            resolve: resolve,
          });
        } else {
          resolve(true);
        }
      });
    },
    [amenitiesData],
  );

  // Set global validation function with useCallback to prevent recreation
  const validateCaracteristicasEditFunction = useCallback(
    async (amenities: any[]) => {
      if (!amenitiesData) {
        return { valid: true }; // Skip validation if amenities not loaded
      }

      // WiFi is mandatory - check if it's included or charged by slug
      const wifiAmenity = amenities.find((a: any) => {
        const amenityData = amenitiesData.amenities
          .flatMap((group) => group.amenities)
          .find((am) => am.id === a.amenityId);
        return amenityData?.slug === "wi-fi";
      });
      const isWifiAvailable =
        wifiAmenity &&
        (wifiAmenity.status === "included" || wifiAmenity.status === "charged");

      if (!isWifiAvailable) {
        // Show WiFi alert - but this will always block progression since there's no "continue" option
        await checkWifiAlert(amenities || []);
        return {
          valid: false,
          errors: {
            selectedAmenities: ["Wi-Fi é obrigatório para continuar."],
          },
        };
      }

      // Then check air conditioning (optional but shows warning)
      const airCondProceed = await checkAirCondAlert(amenities || []);
      if (!airCondProceed) {
        return {
          valid: false,
          errors: {
            selectedAmenities: [
              "Confirme se deseja continuar sem ar-condicionado.",
            ],
          },
        };
      }

      return { valid: true };
    },
    [amenitiesData, checkWifiAlert, checkAirCondAlert],
  );

  // Set global validation function
  React.useEffect(() => {
    globalValidateCaracteristicasEdit = validateCaracteristicasEditFunction;
  }, [validateCaracteristicasEditFunction]);

  // Show loading state
  if (isLoadingAmenities) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-gray-600">Carregando amenidades...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (amenitiesError || propAmenitiesData === null) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <p className="text-red-600 mb-4">
                {amenitiesError || "Erro ao carregar amenidades"}
              </p>
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
              >
                Tentar novamente
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div
        ref={
          errors.selectedAmenities && registerField
            ? (el) => registerField("selectedAmenities", el)
            : undefined
        }
      >
        <AmenitiesForm
          data={amenitiesData!} // Use non-null assertion since we already checked above
          selectedAmenities={selectedAmenities}
          onAmenitiesChange={handleAmenitiesChange}
        />
      </div>

      {errors.selectedAmenities && (
        <p className="text-sm text-red-600 mt-2">
          {errors.selectedAmenities[0]}
        </p>
      )}

      {/* WiFi Alert Modal */}
      <Dialog
        open={wifiAlert.isOpen}
        onOpenChange={(open) => {
          if (!open && wifiAlert.resolve) {
            wifiAlert.resolve(false);
            setWifiAlert({ isOpen: false, resolve: null });
          }
        }}
      >
        <DialogContent className="max-w-md p-0 overflow-hidden border-0">
          <div className="bg-red-500 text-white p-4 rounded-lg">
            <div className="flex flex-col">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-medium text-sm mb-1">
                    Wi-Fi é obrigatório!
                  </h3>
                  <p className="text-sm opacity-90">
                    Sem internet não é possível
                    <br />
                    cadastrar sua sala.
                  </p>
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  className="bg-red-600 text-white hover:bg-red-700 text-xs px-3 py-1 h-auto"
                  onClick={() => {
                    // Find WiFi amenity and scroll to it
                    const wifiAmenity = amenitiesData?.amenities
                      .flatMap((group) => group.amenities)
                      .find((a) => a.slug === "wi-fi");

                    if (wifiAmenity) {
                      // Close modal and resolve promise
                      if (wifiAlert.resolve) {
                        wifiAlert.resolve(false);
                      }
                      setWifiAlert({ isOpen: false, resolve: null });

                      // Small delay to ensure modal is closed, then scroll to WiFi amenity
                      setTimeout(() => {
                        const wifiElement = document.querySelector(
                          `[data-amenity-id="${wifiAmenity.id}"]`,
                        );
                        if (wifiElement) {
                          wifiElement.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });

                          // Highlight the element briefly
                          wifiElement.classList.add(
                            "ring-2",
                            "ring-blue-500",
                            "ring-opacity-75",
                          );
                          setTimeout(() => {
                            wifiElement.classList.remove(
                              "ring-2",
                              "ring-blue-500",
                              "ring-opacity-75",
                            );
                          }, 2000);
                        }
                      }, 300);
                    }
                  }}
                >
                  Adicionar Wi-Fi
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Air Conditioning Alert Modal */}
      <Dialog
        open={airCondAlert.isOpen}
        onOpenChange={(open) => {
          if (!open && airCondAlert.resolve) {
            // Closing modal without clicking a button = proceed (treat as "continue anyway")
            airCondAlert.resolve(true);
            setAirCondAlert({ isOpen: false, resolve: null });
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Alertas</DialogTitle>
            <DialogDescription>
              Usuário marcou ar-condicionado como indisponível
            </DialogDescription>
          </DialogHeader>
          <div className="bg-white border p-3 rounded-md text-sm text-gray-700">
            <p className="font-medium">Cadastrar sem ar-condicionado?</p>
            <p className="text-gray-600 mt-1">
              Salas sem ar-condicionado podem reduzir o<br />
              interesse dos clientes.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                // Find air conditioning amenity and scroll to it
                const airCondAmenity = amenitiesData?.amenities
                  .flatMap((group) => group.amenities)
                  .find((a) => a.slug === "ar-condicionado");

                if (airCondAmenity) {
                  // Close modal and resolve promise
                  if (airCondAlert.resolve) {
                    airCondAlert.resolve(false);
                  }
                  setAirCondAlert({ isOpen: false, resolve: null });

                  // Small delay to ensure modal is closed, then scroll to air conditioning amenity
                  setTimeout(() => {
                    const airCondElement = document.querySelector(
                      `[data-amenity-id="${airCondAmenity.id}"]`,
                    );
                    if (airCondElement) {
                      airCondElement.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                      });

                      // Highlight the element briefly
                      airCondElement.classList.add(
                        "ring-2",
                        "ring-blue-500",
                        "ring-opacity-75",
                      );
                      setTimeout(() => {
                        airCondElement.classList.remove(
                          "ring-2",
                          "ring-blue-500",
                          "ring-opacity-75",
                        );
                      }, 2000);
                    }
                  }, 300);
                } else {
                  if (airCondAlert.resolve) {
                    airCondAlert.resolve(false);
                  }
                  setAirCondAlert({ isOpen: false, resolve: null });
                }
              }}
            >
              Voltar e corrigir
            </Button>
            <Button
              variant="default"
              className="bg-black text-white hover:bg-gray-800"
              onClick={() => {
                if (airCondAlert.resolve) {
                  airCondAlert.resolve(true);
                }
                setAirCondAlert({ isOpen: false, resolve: null });
              }}
            >
              Continuar sem ar-condicionado
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Export validation function for use in step definition
export const validateCaracteristicasEditStep = async (formData: any) => {
  if (globalValidateCaracteristicasEdit) {
    return await globalValidateCaracteristicasEdit(formData.selectedAmenities);
  }
  return { valid: true };
};
